import { Injectable, inject } from '@angular/core';
import { ToastService } from './toast.service';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  description: string;
  showMessage?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class KeyboardToastService {
  private keyboardShortcuts: KeyboardShortcut[] = [
    // Playback Controls
    { key: ' ', description: 'Play/Pause' },
    { key: 'k', description: 'Play/Pause' },
    { key: 'r', description: 'Restart Media' },
    { key: 'm', description: 'Mute/Unmute', showMessage: false },
    { key: 'l', description: 'Loop Current Media' },

    // Navigation
    { key: 'ArrowLeft', description: 'Previous Item' },
    { key: 'a', description: 'Previous Item' },
    { key: 'ArrowRight', description: 'Next Item' },
    { key: 'd', description: 'Next Item' },
    { key: 'Home', description: 'Reset to First Item' },
    { key: 'h', description: 'Reset to First Item' },
    { key: 'PageUp', description: 'Go Back 10 Items' },
    { key: 'PageDown', description: 'Go Forward 10 Items' },

    // Volume
    { key: 'ArrowUp', description: 'Volume Up (+10%)' },
    { key: 'ArrowDown', description: 'Volume Down (-10%)' },

    // Video Seek
    { key: ',', description: 'Seek Backward 5s' },
    { key: '.', description: 'Seek Forward 5s' },
    { key: '0', description: 'Seek to 0%' },
    { key: '1', description: 'Seek to 10%' },
    { key: '2', description: 'Seek to 20%' },
    { key: '3', description: 'Seek to 30%' },
    { key: '4', description: 'Seek to 40%' },
    { key: '5', description: 'Seek to 50%' },
    { key: '6', description: 'Seek to 60%' },
    { key: '7', description: 'Seek to 70%' },
    { key: '8', description: 'Seek to 80%' },
    { key: '9', description: 'Seek to 90%' },

    // Fullscreen
    { key: 'w', description: 'Toggle Full Window Mode' },
    { key: 'f', description: 'Toggle Native Fullscreen' },
    { key: 'Escape', description: 'Exit Fullscreen' },

    // Actions
    { key: 'e', description: 'Open on e621' },
    { key: 's', description: 'Toggle Favorite' },
    { key: 'c', description: 'Hide/Show Controls' },

    // Search shortcuts
    { key: 'Enter', shiftKey: true, description: 'Search' },
    { key: 'Enter', ctrlKey: true, description: 'Fullscreen Search' },

    // Date navigation shortcuts
    { key: '[', description: 'Earlier date period' },
    { key: ']', description: 'Later date period' },
  ];

  private isListening = false;
  private keydownListener?: (event: KeyboardEvent) => void;
  private toastService = inject(ToastService);

  startListening(): void {
    if (this.isListening) return;

    this.keydownListener = (event: KeyboardEvent) => {
      this.handleKeydown(event);
    };

    document.addEventListener('keydown', this.keydownListener);
    this.isListening = true;
  }

  stopListening(): void {
    if (!this.isListening || !this.keydownListener) return;

    document.removeEventListener('keydown', this.keydownListener);
    this.isListening = false;
    this.keydownListener = undefined;
  }

  private handleKeydown(event: KeyboardEvent): void {
    // Skip if typing in input fields
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    // Find matching shortcut
    const shortcut = this.keyboardShortcuts.find(shortcut =>
      shortcut.key.toLowerCase() === event.key.toLowerCase() &&
      !!shortcut.ctrlKey === event.ctrlKey &&
      !!shortcut.shiftKey === event.shiftKey &&
      !!shortcut.altKey === event.altKey
    );

    if (shortcut) {
      this.showToastForShortcut(shortcut);
    }
  }

  private showToastForShortcut(shortcut: KeyboardShortcut): void {
    // Format the key combination for display
    const keyDisplay = this.formatKeyDisplay(shortcut);
    const message = `${keyDisplay} ${shortcut.description}`;

    if (shortcut.showMessage !== false) {
      this.toastService.showToast(message);
    }
  }

  private formatKeyDisplay(shortcut: KeyboardShortcut): string {
    const parts: string[] = [];

    if (shortcut.ctrlKey) parts.push('Ctrl');
    if (shortcut.shiftKey) parts.push('Shift');
    if (shortcut.altKey) parts.push('Alt');

    // Format key name for display
    let keyName = shortcut.key;
    switch (keyName) {
      case ' ':
        keyName = 'Space';
        break;
      case 'ArrowLeft':
        keyName = '←';
        break;
      case 'ArrowRight':
        keyName = '→';
        break;
      case 'ArrowUp':
        keyName = '↑';
        break;
      case 'ArrowDown':
        keyName = '↓';
        break;
      case 'Enter':
        keyName = 'Enter';
        break;
      case 'Escape':
        keyName = 'Esc';
        break;
      case 'PageUp':
        keyName = 'Page Up';
        break;
      case 'PageDown':
        keyName = 'Page Down';
        break;
      case 'Home':
        keyName = 'Home';
        break;
      default:
        keyName = keyName.toUpperCase();
        break;
    }

    parts.push(keyName);

    return parts.join('+');
  }
}
